/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ChevronRightIcon } from '@/shared/ui/radix-icons';
import { MoveStageSheet } from '@/shared/ui/components/composed/mobile-sheet';
import { useSlidingStrip } from '@/shared/hooks/use-sliding-strip';
import { VISIT_ACCENT_COLORS, useVisitStatusLabels } from '../constants/visit-colors';
import type { VisitStatus } from '../constants/visit-colors';
import type { KanbanVisit, VisitKanbanStatus } from '@pages/authenticated/agency-pages/visits/useVisitsKanban';
import { COLUMNS, ColumnCards, CountBadge } from './VisitsKanbanBoard.shared';
import { Text, UnstyledButton } from '@/shared/ui';

export { MobileCarouselLayout } from './VisitsKanbanBoard.carousel';

/**
 * Mobile layout A — status tabs: a horizontally scrollable row of status pills
 * (with counts) selects which single column fills the screen below.
 */
export const MobileTabsLayout: React.FC<{
  visits: KanbanVisit[];
  selectedId: string | null;
  onSelectVisit: (id: string | null) => void;
  onStatusChange: (id: string, status: VisitKanbanStatus) => void;
}> = ({ visits, selectedId, onSelectVisit, onStatusChange }) => {
  const { t } = useTranslation('features/visits');
  const [activeCol, setActiveCol] = useState<VisitStatus>('pending');
  const [moving, setMoving] = useState<KanbanVisit | null>(null);
  const strip = useSlidingStrip();
  const statusLabels = useVisitStatusLabels();
  const cards = visits.filter((v) => v.status === activeCol);
  const accent = VISIT_ACCENT_COLORS[activeCol];

  /**
   * Moves a visit from the sheet, and offers to put it back.
   *
   * Undo replays the same transition in reverse through the same handler
   * rather than restoring a snapshot, so a state the server would refuse on
   * the way back is refused out loud instead of leaving the board showing a
   * status the visit does not have.
   */
  const move = useCallback(async (visit: KanbanVisit, next: VisitKanbanStatus) => {
    const previous = visit.status as VisitKanbanStatus;
    setMoving(null);
    await onStatusChange(visit.id, next);
    toast(
      t('move.moved', {
        name: (visit.client || '').split(' ')[0],
        status: statusLabels[next as VisitStatus],
      }),
      {
        duration: 4500,
        action: {
          label: t('move.undo'),
          onClick: () => { void onStatusChange(visit.id, previous); },
        },
      },
    );
  }, [onStatusChange, statusLabels, t]);

  return (
    <div data-testid="kanban-mobile-tabs" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {/*
        One line that slides, not rows that wrap -- the same reasoning as the
        attendances board: an agency with more states than the prototype
        assumed turned this row into a quarter of the screen before a single
        card was visible.
      */}
      <div
        ref={strip.ref}
        {...strip.props}
        role="tablist"
        className={strip.props.className}
        style={{
          ...strip.props.style,
          display: 'flex', gap: 'var(--space-2)',
          paddingBottom: 'var(--space-1)',
        }}
      >
        {COLUMNS.map((col) => {
          const colAccent = VISIT_ACCENT_COLORS[col];
          const active = col === activeCol;
          const count = visits.filter((v) => v.status === col).length;
          return (
            <UnstyledButton
              key={col}
              role="tab"
              aria-selected={active}
              type="button"
              onClick={() => setActiveCol(col)}
              onClickCapture={(e) => {
                if (strip.wasDragged()) { e.preventDefault(); e.stopPropagation(); }
              }}
              style={{
                display: 'flex', alignItems: 'center', gap: 'var(--space-2)', flexShrink: 0,
                scrollSnapAlign: 'start',
                padding: 'var(--space-2) var(--space-3)', borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-meta)', fontWeight: 'var(--font-semibold)' as unknown as number,
                fontFamily: 'var(--font-family)', cursor: 'pointer', whiteSpace: 'nowrap',
                background: active ? `${colAccent}1A` : 'var(--surface-primary)',
                color: active ? colAccent : 'var(--text-secondary)',
                borderTop: `2px solid ${active ? colAccent : 'transparent'}`,
                border: `1px solid ${active ? colAccent : 'var(--border-subtle)'}`,
              }}
            >
              <Text as="span" style={{ width: 8, height: 8, borderRadius: '50%', background: colAccent }} />
              {statusLabels[col]}
              <CountBadge accent={colAccent} count={count} />
            </UnstyledButton>
          );
        })}
      </div>

      <div
        data-testid="kanban-column"
        style={{
          position: 'relative',
          background: 'var(--surface-primary)', borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border-subtle)',
          boxShadow: 'var(--shadow-sm)', overflow: 'hidden',
        }}
      >
        <div style={{ padding: 'var(--space-3)' }}>
          <Text as="span" style={{ display: 'inline-block', paddingBottom: 'var(--space-1)', borderBottom: `2px solid ${accent}`, fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-bold)' as unknown as number, color: 'var(--text-primary)' }}>
            {statusLabels[activeCol]}
          </Text>
        </div>
        <div style={{ padding: 'var(--space-2)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', minHeight: 120 }}>
          <ColumnCards
            cards={cards}
            selectedId={selectedId}
            onSelectVisit={onSelectVisit}
            onMoveStatus={setMoving}
          />
        </div>
      </div>

      {/*
        The same sheet the attendances list uses. Two boards on one phone that
        change state in two different ways would be a worse cost than sharing
        a component whose only domain knowledge arrives as props.
      */}
      <MoveStageSheet
        open={Boolean(moving)}
        subjectName={moving?.client || ''}
        subjectDetail={moving?.propertyShort || moving?.property}
        currentId={moving?.status ?? ''}
        options={COLUMNS.map((col) => ({
          id: col,
          label: statusLabels[col],
          hint: t(`move.hints.${col}`, { defaultValue: '' }) || undefined,
          color: VISIT_ACCENT_COLORS[col],
        }))}
        onSelect={(status) => {
          if (moving) void move(moving, status as VisitKanbanStatus);
        }}
        onClose={() => setMoving(null)}
      />
    </div>
  );
};

/**
 * Mobile layout B — stacked accordion: every status is a collapsible section,
 * so all columns are visible by scrolling vertically (no hidden horizontal pane).
 */
export const MobileStackLayout: React.FC<{
  cardsByCol: (col: VisitStatus) => KanbanVisit[];
  selectedId: string | null;
  onSelectVisit: (id: string | null) => void;
}> = ({ cardsByCol, selectedId, onSelectVisit }) => {
  const [activeCol, setActiveCol] = useState<VisitStatus>('pending');
  const statusLabels = useVisitStatusLabels();

  return (
    <div data-testid="kanban-mobile-stack" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      {COLUMNS.map((col) => {
        const accent = VISIT_ACCENT_COLORS[col];
        const cards = cardsByCol(col);
        const isOpen = activeCol === col;
        return (
          <div
            key={col}
            data-testid="kanban-column"
            style={{
              position: 'relative',
              background: 'var(--surface-primary)', borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border-subtle)',
              boxShadow: 'var(--shadow-sm)', overflow: 'hidden',
            }}
          >
            <UnstyledButton
              type="button"
              onClick={() => setActiveCol(col)}
              aria-expanded={isOpen}
              style={{
                width: '100%', padding: 'var(--space-3)', display: 'flex', justifyContent: 'space-between',
                alignItems: 'center', borderBottom: isOpen ? '1px solid var(--border-subtle)' : 'none',
                background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'var(--font-family)',
              }}
            >
              <Text as="span" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', paddingBottom: 'var(--space-1)', borderBottom: `2px solid ${accent}`, fontSize: 'var(--text-body-sm)', fontWeight: 'var(--font-bold)' as unknown as number, color: 'var(--text-primary)' }}>
                <ChevronRightIcon
                  width={16}
                  height={16}
                  style={{ transform: isOpen ? 'rotate(90deg)' : 'none', transition: 'transform 0.15s', color: 'var(--text-tertiary)' } as React.CSSProperties}
                />
                {statusLabels[col]}
              </Text>
              <CountBadge accent={accent} count={cards.length} />
            </UnstyledButton>
            {isOpen && (
              <div style={{ padding: 'var(--space-2)', display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                <ColumnCards cards={cards} selectedId={selectedId} onSelectVisit={onSelectVisit} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
